import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../i18n/useTranslation';
import { Card } from '../../atoms/Card/Card';
import { Input } from '../../atoms/Input/Input';
import { Select } from '../../atoms/Select/Select';
import { Button } from '../../atoms/Button/Button';
import { Heading } from '../../atoms/Typography/Heading';
import { Text } from '../../atoms/Typography/Text';
import { Inline } from '../../atoms/Inline/Inline';
import { Stack } from '../../atoms/Stack/Stack';
import { Alert } from '../../atoms/Alert/Alert';
import { LiveRegion } from '../../atoms/LiveRegion/LiveRegion';
import { EmptyState } from '../../atoms/EmptyState';
import { Icon } from '../../atoms/Icon/Icon';
import { LoadErrorState } from '../../feedback/LoadErrorState/LoadErrorState';
import { ApiError } from '../../../lib/apiClient';
import {
  chooseFocustronicDevice,
  connectFocustronic,
  disconnectFocustronic,
  getFocustronicConnection,
  getFocustronicMappings,
  listFocustronicDevices,
  listFocustronicTargets,
  previewFocustronicDevice,
  focustronicConnectionKey,
  focustronicMappingsKey,
  focustronicPreviewKey,
  focustronicDevicesKey,
  focustronicTargetsKey,
  saveFocustronicMappings,
  syncFocustronic,
  type FocustronicImportResult,
  type FocustronicMappingInput,
  type FocustronicSyncSummary,
} from '../../../services/focustronicService';
import { TestMappingTable } from '../TestMapping/TestMappingTable';
import { mappingInputs, type MappingDraft, type MappingDrafts } from '../TestMapping/testMappingDraft';
import { useToast } from '../../../stores/toastStore';
import './FocustronicPanel.css';

export interface FocustronicPanelProps {
  /** ReefGuide aquarium the Mastertronic's results are imported into. */
  aquariumId: string | undefined;
}

const ERRORS = 'integrations.focustronic.errors';

/** Translation key of an error (never raw backend text). */
export function focustronicErrorKey(err: unknown): string {
  const code = err instanceof ApiError ? err.code : typeof err === 'string' ? err : '';
  switch (code) {
    case 'FOCUSTRONIC_AUTH_FAILED':
      return `${ERRORS}.auth`;
    case 'FOCUSTRONIC_DEVICE_NOT_FOUND':
      return `${ERRORS}.deviceNotFound`;
    case 'FOCUSTRONIC_UNAVAILABLE':
    case 'FOCUSTRONIC_NETWORK_ERROR':
      return `${ERRORS}.unavailable`;
    case 'FOCUSTRONIC_NOT_CONNECTED':
      return `${ERRORS}.notConnected`;
    case 'RATE_LIMITED':
      return `${ERRORS}.rateLimited`;
    default:
      return err instanceof ApiError && err.status === 429 ? `${ERRORS}.rateLimited` : `${ERRORS}.generic`;
  }
}

function formatDateTime(iso: string, language: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(language, { dateStyle: 'medium', timeStyle: 'short' });
}

export function FocustronicPanel({ aquariumId }: FocustronicPanelProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const headingId = useId();
  const id = aquariumId ?? '';

  // The password lives only in this component's state: never in a query key,
  // the query cache or web storage. It is cleared once the account is saved.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [changingDevice, setChangingDevice] = useState(false);
  const [deviceChoice, setDeviceChoice] = useState<string | null>(null);
  // Unsaved mapping choices, by test (sourceKey); cleared when saved or the Mastertronic changes.
  const [drafts, setDrafts] = useState<MappingDrafts>({});

  const connectionQuery = useQuery({
    queryKey: focustronicConnectionKey(id),
    queryFn: () => getFocustronicConnection(id),
    enabled: id !== '',
  });
  const connection = connectionQuery.data;
  const connected = connection?.connected === true;
  const activeDevice = connection?.deviceId ?? '';
  const choosingDevice = connected && !replacing && (activeDevice === '' || changingDevice);

  const devicesQuery = useQuery({
    queryKey: focustronicDevicesKey(id),
    queryFn: () => listFocustronicDevices(id),
    enabled: choosingDevice,
    retry: false,
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });
  const devices = devicesQuery.data ?? [];
  // A single Mastertronic is preselected so its mapping shows at once; nothing is imported before "activate".
  const selectedDevice = deviceChoice ?? (devices.length === 1 ? devices[0]?.id ?? '' : '');
  const previewDevice = choosingDevice && selectedDevice !== '' && selectedDevice !== activeDevice ? selectedDevice : '';

  const previewQuery = useQuery({
    queryKey: focustronicPreviewKey(id, previewDevice),
    queryFn: () => previewFocustronicDevice(id, previewDevice),
    enabled: previewDevice !== '',
    retry: false,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
  const preview = previewQuery.data ?? [];

  const showMappings = connected && !replacing && activeDevice !== '' && !changingDevice;
  const mappingsQuery = useQuery({
    queryKey: focustronicMappingsKey(id),
    queryFn: () => getFocustronicMappings(id),
    enabled: showMappings,
  });
  const mappings = mappingsQuery.data ?? [];
  const newTests = mappings.filter((m) => m.status === 'new').length;

  const targetsQuery = useQuery({
    queryKey: focustronicTargetsKey(id),
    queryFn: () => listFocustronicTargets(id),
    enabled: previewDevice !== '' || showMappings,
    staleTime: Infinity,
  });
  const targets = targetsQuery.data ?? [];

  const previewInputs = mappingInputs(preview, drafts);
  const importable = previewInputs?.filter((m) => m.parameterId !== null || m.customParameter).length ?? 0;
  // Stored mappings as they are — "accept suggestions" confirms every new test unchanged.
  const mappingsInputs = mappingInputs(mappings, {});

  function setDraft(sourceKey: string, draft: MappingDraft) {
    setDrafts((current) => ({ ...current, [sourceKey]: draft }));
  }

  function afterImport(summary: FocustronicSyncSummary) {
    if (summary.status === 'failed') return;
    if (summary.imported + summary.updated > 0) {
      void queryClient.invalidateQueries({ queryKey: ['measurements', id] });
    }
    showToast(t('integrations.focustronic.sync.done', { imported: summary.imported, updated: summary.updated }), 'success');
  }

  const connectMutation = useMutation({
    mutationFn: () => connectFocustronic(id, email, password),
    onSuccess: (result) => {
      setPassword('');
      setTouched(false);
      setReplacing(false);
      setChangingDevice(false);
      setDeviceChoice(null);
      queryClient.setQueryData(focustronicConnectionKey(id), result.connection);
      queryClient.setQueryData(focustronicDevicesKey(id), result.devices);
    },
  });

  function afterSave(result: FocustronicImportResult) {
    setDrafts({});
    // A parameter created for a test is offered for other tests from now on.
    void queryClient.invalidateQueries({ queryKey: focustronicTargetsKey(id) });
    queryClient.setQueryData(focustronicConnectionKey(id), result.connection);
    queryClient.setQueryData(focustronicMappingsKey(id), result.mappings);
    afterImport(result.sync);
  }

  const activateMutation = useMutation({
    mutationFn: (input: { deviceId: string; mappings: FocustronicMappingInput[] }) => chooseFocustronicDevice(id, input.deviceId, input.mappings),
    onSuccess: (result) => {
      setDeviceChoice(null);
      setChangingDevice(false);
      afterSave(result);
    },
  });

  const saveMutation = useMutation({
    mutationFn: (input: FocustronicMappingInput[]) => saveFocustronicMappings(id, input),
    onSuccess: afterSave,
  });

  /** Saves one test's mapping from the dialog (only that test — other new tests stay unconfirmed); rejects when it failed. */
  async function saveRow(sourceKey: string, draft: MappingDraft): Promise<void> {
    const inputs = mappingInputs(mappings.filter((m) => m.sourceKey === sourceKey), { [sourceKey]: draft });
    if (!inputs) throw new Error('invalid mapping');
    await saveMutation.mutateAsync(inputs);
  }

  const syncMutation = useMutation({
    mutationFn: () => syncFocustronic(id),
    onSuccess: (result) => {
      queryClient.setQueryData(focustronicConnectionKey(id), result.connection);
      afterImport(result.sync);
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => disconnectFocustronic(id),
    onSuccess: () => {
      setReplacing(false);
      setChangingDevice(false);
      setDeviceChoice(null);
      queryClient.removeQueries({ queryKey: ['focustronic', id] });
      void queryClient.invalidateQueries({ queryKey: focustronicConnectionKey(id) });
    },
  });

  const isBusy = connectMutation.isPending || activateMutation.isPending || saveMutation.isPending
    || syncMutation.isPending || disconnectMutation.isPending;

  // ── Account form ──────────────────────────────────────────────────────────
  const emailError = touched && !/^\S+@\S+\.\S+$/.test(email.trim()) ? t('integrations.focustronic.account.emailError') : undefined;
  const passwordError = touched && password === '' ? t('integrations.focustronic.account.passwordError') : undefined;

  function handleConnect(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (isBusy || !/^\S+@\S+\.\S+$/.test(email.trim()) || password === '') return;
    disconnectMutation.reset();
    connectMutation.mutate();
  }

  // ── Render ────────────────────────────────────────────────────────────────
  if (connectionQuery.isPending) {
    return (
      <Card variant="outlined" className="focustronic-panel">
        <Heading level={2}>{t('integrations.focustronic.title')}</Heading>
        <LiveRegion><Text color="secondary">{t('integrations.focustronic.loading')}</Text></LiveRegion>
      </Card>
    );
  }

  if (connectionQuery.isError) {
    return (
      <Card variant="outlined" className="focustronic-panel">
        <Heading level={2}>{t('integrations.focustronic.title')}</Heading>
        <LoadErrorState
          title={t('integrations.focustronic.loadError')}
          titleLevel={3}
          onRetry={() => { void connectionQuery.refetch(); }}
          retryLabel={t('integrations.focustronic.retry')}
          retrying={connectionQuery.isFetching}
        />
      </Card>
    );
  }

  const lastSync = connection?.lastSync ?? null;
  const showForm = !connected || replacing;

  const announcement = connectMutation.isPending
    ? t('integrations.focustronic.account.connecting')
    : activateMutation.isPending || saveMutation.isPending || syncMutation.isPending
      ? t('integrations.focustronic.sync.running')
      : disconnectMutation.isSuccess
        ? t('integrations.focustronic.account.disconnected')
        : '';

  return (
    <Card variant="outlined" className="focustronic-panel">
      <Stack gap="2">
        <Heading level={2}>{t('integrations.focustronic.title')}</Heading>
        <Text color="secondary">{t('integrations.focustronic.description')}</Text>
      </Stack>

      <LiveRegion visuallyHidden>{announcement}</LiveRegion>

      {/* ── Focustronic account ─────────────────────────────────────────── */}
      <section className="focustronic-panel__section" aria-labelledby={`${headingId}-account`}>
        <Heading level={3} id={`${headingId}-account`}>{t('integrations.focustronic.account.heading')}</Heading>

        {showForm ? (
          <form className="focustronic-panel__form" onSubmit={handleConnect} noValidate>
            <Input
              type="email"
              label={t('integrations.focustronic.account.email')}
              value={email}
              onChange={(e) => { setEmail(e.target.value); }}
              error={emailError}
              disabled={isBusy}
              required
              autoComplete="off"
              maxLength={255}
              fullWidth
            />
            <Input
              type="password"
              label={t('integrations.focustronic.account.password')}
              value={password}
              onChange={(e) => { setPassword(e.target.value); }}
              error={passwordError}
              helperText={t('integrations.focustronic.account.passwordHelp')}
              disabled={isBusy}
              required
              autoComplete="off"
              maxLength={200}
              fullWidth
            />
            {connectMutation.isError && <Alert compact>{t(focustronicErrorKey(connectMutation.error))}</Alert>}
            <Inline gap="3" wrap>
              <Button type="submit" variant="primary" isLoading={connectMutation.isPending} disabled={isBusy}>
                {t('integrations.focustronic.account.connect')}
              </Button>
              {replacing && (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isBusy}
                  onClick={() => { setReplacing(false); setPassword(''); setTouched(false); connectMutation.reset(); }}
                >
                  {t('integrations.focustronic.account.cancel')}
                </Button>
              )}
            </Inline>
          </form>
        ) : (
          <Stack gap="3">
            <Text>{t('integrations.focustronic.account.connectedAs', { email: connection?.email ?? '' })}</Text>
            {disconnectMutation.isError && <Alert compact>{t(focustronicErrorKey(disconnectMutation.error))}</Alert>}
            <Inline gap="3" wrap>
              <Button
                type="button"
                variant="ghost"
                disabled={isBusy}
                onClick={() => { setReplacing(true); setEmail(connection?.email ?? ''); disconnectMutation.reset(); }}
              >
                {t('integrations.focustronic.account.change')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={isBusy}
                isLoading={disconnectMutation.isPending}
                onClick={() => { disconnectMutation.mutate(); }}
              >
                {t('integrations.focustronic.account.disconnect')}
              </Button>
            </Inline>
            <Text variant="body-sm" color="secondary">{t('integrations.focustronic.account.disconnectNote')}</Text>
          </Stack>
        )}
      </section>

      {/* ── Mastertronic: choose, map its tests, activate ───────────────── */}
      {choosingDevice && (
        <section className="focustronic-panel__section" aria-labelledby={`${headingId}-device`}>
          <Heading level={3} id={`${headingId}-device`}>{t('integrations.focustronic.device.heading')}</Heading>
          <Text color="secondary">{t('integrations.focustronic.device.intro')}</Text>

          {devicesQuery.isPending && (
            <LiveRegion><Text color="secondary">{t('integrations.focustronic.device.loading')}</Text></LiveRegion>
          )}

          {devicesQuery.isError && (
            <LoadErrorState
              title={t(focustronicErrorKey(devicesQuery.error))}
              titleLevel={4}
              onRetry={() => { void devicesQuery.refetch(); }}
              retryLabel={t('integrations.focustronic.retry')}
              retrying={devicesQuery.isFetching}
            />
          )}

          {devicesQuery.isSuccess && devices.length === 0 && (
            <EmptyState
              icon={<Icon name="droplet" />}
              headline={t('integrations.focustronic.device.emptyTitle')}
              description={t('integrations.focustronic.device.emptyBody')}
              actionLabel={t('integrations.focustronic.device.checkAgain')}
              onAction={() => { void devicesQuery.refetch(); }}
              actionVariant="secondary"
              headingLevel={4}
            />
          )}

          {devicesQuery.isSuccess && devices.length > 0 && (
            <div className="focustronic-panel__device-select">
              <Select
                label={t('integrations.focustronic.device.label')}
                placeholder={t('integrations.focustronic.device.placeholder')}
                value={selectedDevice}
                onChange={(e) => { setDeviceChoice(e.target.value); setDrafts({}); activateMutation.reset(); }}
                disabled={isBusy}
                fullWidth
                options={devices.map((d) => ({ value: d.id, label: d.serialNumber ? `${d.name} (${d.serialNumber})` : d.name }))}
              />
            </div>
          )}

          {previewDevice !== '' && previewQuery.isPending && (
            <LiveRegion><Text color="secondary">{t('integrations.focustronic.preview.loading')}</Text></LiveRegion>
          )}

          {previewDevice !== '' && previewQuery.isError && (
            <LoadErrorState
              title={t(focustronicErrorKey(previewQuery.error))}
              titleLevel={4}
              onRetry={() => { void previewQuery.refetch(); }}
              retryLabel={t('integrations.focustronic.retry')}
              retrying={previewQuery.isFetching}
            />
          )}

          {previewDevice !== '' && previewQuery.isSuccess && preview.length === 0 && (
            <EmptyState
              icon={<Icon name="droplet" />}
              headline={t('integrations.focustronic.preview.emptyTitle')}
              description={t('integrations.focustronic.preview.emptyBody')}
              actionLabel={t('integrations.focustronic.preview.checkAgain')}
              onAction={() => { void previewQuery.refetch(); }}
              actionVariant="secondary"
              headingLevel={4}
            />
          )}

          {previewDevice !== '' && previewQuery.isSuccess && preview.length > 0 && targetsQuery.isError && (
            <LoadErrorState
              title={t('integrations.testMapping.loadError')}
              titleLevel={4}
              onRetry={() => { void targetsQuery.refetch(); }}
              retryLabel={t('integrations.focustronic.retry')}
              retrying={targetsQuery.isFetching}
            />
          )}

          {previewDevice !== '' && previewQuery.isSuccess && preview.length > 0 && targetsQuery.isSuccess && (
            <Stack gap="4">
              <Text color="secondary">{t('integrations.testMapping.intro')}</Text>
              <TestMappingTable rows={preview} targets={targets} drafts={drafts} onSubmitRow={setDraft} disabled={isBusy} />
              {importable === 0 ? (
                <Alert tone="warning" compact live="off">{t('integrations.testMapping.nothingToImport')}</Alert>
              ) : (
                <Text variant="body-sm" color="secondary">{t('integrations.testMapping.confirmHint')}</Text>
              )}
            </Stack>
          )}

          {activateMutation.isError && <Alert compact>{t(focustronicErrorKey(activateMutation.error))}</Alert>}

          {(previewDevice !== '' || changingDevice) && (
            <Inline gap="3" wrap>
              {previewDevice !== '' && (
                <Button
                  type="button"
                  variant="primary"
                  isLoading={activateMutation.isPending}
                  disabled={isBusy || !previewQuery.isSuccess || !targetsQuery.isSuccess || previewInputs === null || importable === 0}
                  onClick={() => { if (previewInputs) activateMutation.mutate({ deviceId: previewDevice, mappings: previewInputs }); }}
                >
                  {t('integrations.testMapping.activate')}
                </Button>
              )}
              {changingDevice && (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isBusy}
                  onClick={() => { setChangingDevice(false); setDeviceChoice(null); setDrafts({}); activateMutation.reset(); }}
                >
                  {t('integrations.focustronic.device.cancel')}
                </Button>
              )}
            </Inline>
          )}
        </section>
      )}

      {/* ── Test mapping of the chosen Mastertronic ─────────────────────── */}
      {showMappings && (
        <section className="focustronic-panel__section" aria-labelledby={`${headingId}-mapping`}>
          <Heading level={3} id={`${headingId}-mapping`}>{t('integrations.testMapping.heading')}</Heading>
          <Text color="secondary">{t('integrations.testMapping.intro')}</Text>

          {(mappingsQuery.isPending || (mappings.length > 0 && targetsQuery.isPending)) && (
            <LiveRegion><Text color="secondary">{t('integrations.testMapping.loading')}</Text></LiveRegion>
          )}

          {(mappingsQuery.isError || targetsQuery.isError) && (
            <LoadErrorState
              title={t('integrations.testMapping.loadError')}
              titleLevel={4}
              onRetry={() => { void mappingsQuery.refetch(); void targetsQuery.refetch(); }}
              retryLabel={t('integrations.focustronic.retry')}
              retrying={mappingsQuery.isFetching || targetsQuery.isFetching}
            />
          )}

          {mappingsQuery.isSuccess && mappings.length === 0 && (
            <EmptyState
              icon={<Icon name="droplet" />}
              headline={t('integrations.testMapping.emptyTitle')}
              description={t('integrations.testMapping.emptyBody')}
              actionLabel={t('integrations.focustronic.sync.button')}
              onAction={() => { syncMutation.mutate(); }}
              actionVariant="secondary"
              headingLevel={4}
            />
          )}

          {mappingsQuery.isSuccess && mappings.length > 0 && targetsQuery.isSuccess && (
            <Stack gap="4">
              {newTests > 0 && (
                <Alert
                  tone="info"
                  compact
                  live="off"
                  action={(
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      isLoading={saveMutation.isPending}
                      disabled={isBusy || mappingsInputs === null}
                      onClick={() => { if (mappingsInputs) saveMutation.mutate(mappingsInputs); }}
                    >
                      {t('integrations.testMapping.acceptSuggestions')}
                    </Button>
                  )}
                >
                  {t('integrations.testMapping.newTests', { count: newTests })}
                </Alert>
              )}
              {saveMutation.isError && <Alert compact>{t('integrations.testMapping.saveError')}</Alert>}
              <TestMappingTable
                rows={mappings}
                targets={targets}
                drafts={{}}
                disabled={isBusy}
                isSubmitting={saveMutation.isPending}
                submitError={saveMutation.isError}
                onEditStart={() => { saveMutation.reset(); }}
                onSubmitRow={(sourceKey, draft) => saveRow(sourceKey, draft)}
              />
              <Text variant="body-sm" color="secondary">{t('integrations.testMapping.saveNote')}</Text>
            </Stack>
          )}
        </section>
      )}

      {/* ── Chosen Mastertronic & import ────────────────────────────────── */}
      {showMappings && (
        <section className="focustronic-panel__section" aria-labelledby={`${headingId}-sync`}>
          <Heading level={3} id={`${headingId}-sync`}>{t('integrations.focustronic.sync.heading')}</Heading>
          <Text variant="body-sm" color="secondary">
            {t('integrations.focustronic.sync.automatic', { device: connection?.deviceName ?? '' })}
          </Text>
          {syncMutation.isError && <Alert compact>{t(focustronicErrorKey(syncMutation.error))}</Alert>}
          <Inline gap="3" wrap>
            <Button
              type="button"
              variant="primary"
              onClick={() => { syncMutation.mutate(); }}
              isLoading={syncMutation.isPending}
              disabled={isBusy}
            >
              {syncMutation.isPending ? t('integrations.focustronic.sync.running') : t('integrations.focustronic.sync.button')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={isBusy}
              onClick={() => { setChangingDevice(true); setDeviceChoice(null); setDrafts({}); syncMutation.reset(); }}
            >
              {t('integrations.focustronic.device.change')}
            </Button>
          </Inline>
          <Stack gap="1">
            {lastSync ? (
              <>
                <Text variant="body-sm">
                  {t('integrations.focustronic.sync.lastSync', { date: formatDateTime(lastSync.at, i18n.language) })}
                </Text>
                {lastSync.status === 'failed' ? (
                  <Alert tone="warning" compact live="off">{t(focustronicErrorKey(lastSync.code ?? ''))}</Alert>
                ) : (
                  <Text variant="body-sm" color="secondary">
                    {t('integrations.focustronic.sync.summary', {
                      imported: lastSync.imported, updated: lastSync.updated, removed: lastSync.removed, skipped: lastSync.skipped, unmapped: lastSync.unmapped ?? 0,
                    })}
                  </Text>
                )}
              </>
            ) : (
              <Text variant="body-sm" color="secondary">{t('integrations.focustronic.sync.never')}</Text>
            )}
          </Stack>
        </section>
      )}

      <Text variant="body-sm" color="secondary">{t('integrations.focustronic.unofficial')}</Text>
    </Card>
  );
}
